// Inspection history timeline on an asbestos record + "Log inspection" form.
import { useState } from "react";
import { History, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ASBESTOS_CONDITIONS, ASBESTOS_RISK_RATINGS } from "./constants";

export function ZoneInspectionHistory({ zone, canEdit, onLocalChange }) {
    const history = zone.inspection_history || [];
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [condition, setCondition] = useState(zone.condition || "");
    const [riskRating, setRiskRating] = useState(zone.risk_rating || "");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    async function submit() {
        setSaving(true);
        try {
            const { data } = await api.post(`/asbestos/zones/${zone.id}/inspections`, {
                date, condition: condition || null, risk_rating: riskRating || null, note: note || null,
            });
            onLocalChange({
                inspection_history: data.inspection_history, condition: data.condition,
                risk_rating: data.risk_rating, survey_date: data.survey_date,
            });
            setNote("");
            setOpen(false);
            toast.success("Inspection logged");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not log inspection");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-1">
                <div className="text-[10.5px] uppercase tracking-wider font-bold" style={{ color: "#9A968F" }}>Inspection history</div>
                {canEdit && !open && (
                    <button data-testid="asbestos-zone-log-inspection" onClick={() => setOpen(true)}
                        className="text-[10.5px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[6px]" style={{ color: "#C8632A", background: "#FBEDE6" }}>
                        <Plus className="w-3 h-3" /> Log inspection
                    </button>
                )}
            </div>
            {open && (
                <div className="rounded-[8px] border p-2 space-y-1.5 mb-2" style={{ borderColor: "#E2DDD5" }}>
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold" style={{ color: "#1F1F1F" }}>New inspection</span>
                        <button onClick={() => setOpen(false)}><X className="w-3.5 h-3.5" style={{ color: "#9A968F" }} /></button>
                    </div>
                    <input type="date" data-testid="asbestos-inspection-date" value={date} onChange={(e) => setDate(e.target.value)}
                        className="w-full text-[11.5px] rounded-[6px] border px-2 py-1" style={{ borderColor: "#E2DDD5" }} />
                    <select data-testid="asbestos-inspection-condition" value={condition} onChange={(e) => setCondition(e.target.value)}
                        className="w-full text-[11.5px] rounded-[6px] border px-2 py-1" style={{ borderColor: "#E2DDD5" }}>
                        <option value="">Condition — unchanged</option>
                        {ASBESTOS_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select data-testid="asbestos-inspection-risk" value={riskRating} onChange={(e) => setRiskRating(e.target.value)}
                        className="w-full text-[11.5px] rounded-[6px] border px-2 py-1" style={{ borderColor: "#E2DDD5" }}>
                        <option value="">Risk rating — unchanged</option>
                        {ASBESTOS_RISK_RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <textarea data-testid="asbestos-inspection-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder="Findings from this inspection…" className="w-full text-[11.5px] rounded-[6px] border px-2 py-1" style={{ borderColor: "#E2DDD5" }} />
                    <button data-testid="asbestos-inspection-save" disabled={saving} onClick={submit}
                        className="w-full text-[11px] py-1.5 rounded-[6px] text-white disabled:opacity-60" style={{ background: "#C8632A" }}>
                        {saving ? "Saving…" : "Save inspection"}
                    </button>
                </div>
            )}
            {history.length === 0 ? (
                <div className="text-[11.5px] italic" style={{ color: "#9A968F" }}>No inspections logged yet.</div>
            ) : (
                <div className="space-y-1.5 max-h-[160px] overflow-y-auto">
                    {history.map((h) => (
                        <div key={h.id} data-testid={`asbestos-inspection-${h.id}`} className="rounded-[7px] px-2 py-1.5 text-[11px]" style={{ background: "#F6F4F0" }}>
                            <div className="flex items-center gap-1.5 font-semibold" style={{ color: "#1F1F1F" }}>
                                <History className="w-3 h-3" style={{ color: "#9A968F" }} /> {h.date || "—"}
                                {h.condition && <span className="opacity-70">· {h.condition}</span>}
                                {h.risk_rating && <span className="opacity-70">· {h.risk_rating} risk</span>}
                            </div>
                            {h.note && <div className="mt-0.5" style={{ color: "#6F6A64" }}>{h.note}</div>}
                            {h.by_name && <div className="mt-0.5 text-[10px]" style={{ color: "#9A968F" }}>Logged by {h.by_name}</div>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
